import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { BoardLabels, ClockLabels, OutcomeLabels } from '@qlash-it/qchess-board-ui';

import { MOTIVI_ESITO } from '@/lib/chess/tavolo';

/**
 * Le parole del pacchetto `@qlash-it/qchess-board-ui`, che non ne scrive nessuna (CONTRATTO §6): scacchiera, orologio, esito.
 * Un'etichetta mancante rende NIENTE, quindi i `reason` si enumerano tutti da `MOTIVI_ESITO` (il gate confronta l'elenco con
 * le chiavi dei cataloghi nelle quattro lingue). I testi sono una PROPOSTA: la revisione e' di lingue-orcb, via qchess-orcr.
 */
const PEZZI = ['wK', 'wQ', 'wR', 'wB', 'wN', 'wP', 'bK', 'bQ', 'bR', 'bB', 'bN', 'bP'] as const;

export function useEtichetteScacchi(): { board: BoardLabels; clock: ClockLabels; outcome: OutcomeLabels } {
  const { t } = useTranslation('chessMatch');
  return useMemo(() => {
    const pieces = Object.fromEntries(PEZZI.map((p) => [p, t(`board.pieces.${p}`)]));
    return {
      board: {
        board: t('board.label'),
        square: (name, piece) => (piece === null ? t('board.squareEmpty', { name }) : t('board.square', { name, piece })),
        pieces,
        promotion: {
          title: t('board.promotion.title'),
          queen: t('board.promotion.queen'),
          rook: t('board.promotion.rook'),
          bishop: t('board.promotion.bishop'),
          knight: t('board.promotion.knight'),
          cancel: t('board.promotion.cancel'),
        },
      },
      clock: { unlimited: t('clock.unlimited') },
      outcome: {
        kind: {
          win: t('outcome.title.win'),
          loss: t('outcome.title.loss'),
          draw: t('outcome.title.draw'),
          no_result: t('outcome.title.noResult'),
        },
        winFor: (winner) => t(`outcome.title.winFor.${winner}`),
        reason: Object.fromEntries(MOTIVI_ESITO.map((m) => [m, t(`outcome.reason.${m}`)])),
      },
    };
  }, [t]);
}
